import { styled } from 'styled-components';

import { CheckboxField } from '#/components/Form/CheckboxField';
import { CurrencyInputField } from '#/components/Form/CurrencyInputField';
import { FormContextConsumer } from '#/components/Form/FormContext';
import { InputField } from '#/components/Form/InputField';
import { JobTitleSelectorField } from '#/components/Form/JobTitleSelectorField';
import { LocationField } from '#/components/Form/LocationField';
import { MultiCheckboxField } from '#/components/Form/MultiCheckboxField';
import { RadioButtonsField } from '#/components/Form/Radio/RadioButtonsField';
import { SelectInputField } from '#/components/Form/SelectInputField';
import { LegacyTooltip } from '#/components/LegacyTooltip/LegacyTooltip';
import { BodySmall } from '#/components/Text/BodySmall';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nFn } from '#/i18n/types/I18nFn';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import { colors } from '#/theme/theme';
import type { DatetimeString } from '#/types/TypedDateString';
import {
  V8_AGES,
  V8_EDUCATION_LEVELS_COMP_SURVEY,
  V8_EMPLOYER_ORG_TYPES,
  V8_GENDERS,
  V8_NUMBER_OF_FT_EMPLOYEES_BUCKETS,
  V8_RACES_AND_ETHNICITIES,
} from '#/utils/constants/compensationDatabase/fields';
import { serializeAddress } from '#/utils/googleAddresses';
import { RichError } from '#/utils/RichError';
import { clientOnlyLocalDateAndTimeString } from '#/utils/string/datetime/convertDatetimeString';
import { humanDate } from '#/utils/string/datetime/dateString';
import { trackError } from '#/utils/trackError';

import { CompensationSurveyFields_Box_1 } from './CompensationSurveyFields.styled';
import { WorkCityStateUnified } from './WorkCityStateUnified';

const Info = styled.div`
  font-size: 14px;
  padding: 13px 22px;
  color: ${colors.mediumContentGrey};
  background-color: ${colors.calloutGrey};
  border-radius: 8px;
`;

function REMOTE_STATUSES(i18n: I18nFn) {
  return [
    {
      label: i18n('On-site'),
      value: 'ONSITE',
    },
    {
      label: i18n('Remote'),
      value: 'REMOTE',
    },
    {
      label: i18n('Hybrid'),
      value: 'HYBRID',
    },
  ];
}

function SALARY_BASED_ON_OPTIONS(i18n: I18nFn) {
  return [
    {
      label: i18n('Your location'),
      value: 'OWN_LOCATION',
    },
    {
      label: i18n("Your employer's location"),
      value: 'EMPLOYER_LOCATION',
    },
  ];
}

function EmployerCityStateUnified({
  values,
  handlePlaceDetails,
  updateValueWith,
}: {
  values: { salaryBasedOn?: string; employerCityStateUnified?: string };
  handlePlaceDetails?: (
    place: google.maps.GeocoderResult,
    isEmployer: boolean,
  ) => void;
  updateValueWith?: (
    arg0: string | string[], // TODO: Fix this the next time the file is edited.
    // oxlint-disable-next-line typescript/no-explicit-any
    arg1: (arg0: Record<string, any>) => unknown,
  ) => void;
}) {
  const { i18n } = useI18n();
  let label;
  if (values.salaryBasedOn === 'EMPLOYER_LOCATION') {
    label = `${i18n('Select the location of the organization')}*`;
  } else if (values.salaryBasedOn === 'OWN_LOCATION') {
    label = i18n('Select the location of the organization');
  }

  return (
    <LocationField
      name="employerCityStateUnified"
      addressTypes={['locality']}
      handlePlaceDetails={(place) => {
        // @ts-expect-error TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        handlePlaceDetails(place, true);
      }}
      label={label}
      noWrap
      placeholder={i18n('e.g. Lake Wobegon, MN')}
      includedRegionCodes={['US']}
      customErrorMatch={(errors) => errors.employerStateCode}
      locationText={values.employerCityStateUnified}
      onClear={() =>
        // @ts-expect-error TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        updateValueWith(
          [
            'employerCity',
            'employerStateCode',
            'employerLatitude',
            'employerLongitude',
          ],

          () => ({
            employerCity: null,
            employerStateCode: null,
            employerLatitude: null,
            employerLongitude: null,
          }),
        )
      }
    />
  );
}

export const getCompensationSurveyFields = (
  i18n: I18nFn,
  locale: I18nLocale,
  updateValueWith: (
    arg0: string | string[],
    // TODO: Fix this the next time the file is edited.
    // oxlint-disable-next-line typescript/no-explicit-any
    arg1: (arg0: Record<string, any>) => unknown,
  ) => void,
  lastSubmitted: DatetimeString | null | undefined,
  searchApiKey: string,
) => {
  const selectEmployerLocationAutoCompleteSuggestion = ({
    city,
    stateCode,
    latitude,
    longitude,
  }: {
    city?: string;
    stateCode?: string;
    latitude?: number;
    longitude?: number;
  }) => {
    updateValueWith(
      [
        'employerStateCode',
        'employerCity',
        'employerLatitude',
        'employerLongitude',
      ],

      ({
        employerCity,
        employerStateCode,
        employerLatitude,
        employerLongitude,
      }) => ({
        // TODO: Fix this the next time the file is edited.
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
        employerCity: city ?? employerCity,
        // TODO: Fix this the next time the file is edited.
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
        employerStateCode: stateCode ?? employerStateCode,
        // TODO: Fix this the next time the file is edited.
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
        employerLatitude: latitude ?? employerLatitude,
        // TODO: Fix this the next time the file is edited.
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
        employerLongitude: longitude ?? employerLongitude,
        employerCityStateUnified: city && stateCode && `${city}, ${stateCode}`,
      }),
    );
  };

  const selectWorkLocationAutoCompleteSuggestion = ({
    city,
    stateCode,
    latitude,
    longitude,
    geoType,
  }: {
    city?: string;
    stateCode?: string;
    latitude?: number;
    longitude?: number;
    geoType?: string;
  }) => {
    updateValueWith(
      [
        'workStateCode',
        'workCity',
        'workLatitude',
        'workLongitude',
        'workGeoType',
      ],

      ({
        workCity,
        workStateCode,
        workLatitude,
        workLongitude,
        workGeoType,
      }) => ({
        // TODO: Fix this the next time the file is edited.
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
        workCity: city ?? workCity,
        // TODO: Fix this the next time the file is edited.
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
        workStateCode: stateCode ?? workStateCode,
        // TODO: Fix this the next time the file is edited.
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
        workLatitude: latitude ?? workLatitude,
        // TODO: Fix this the next time the file is edited.
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
        workLongitude: longitude ?? workLongitude,
        workCityStateUnified: city && stateCode && `${city}, ${stateCode}`,
        // TODO: Fix this the next time the file is edited.
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
        workGeoType: geoType ?? workGeoType,
      }),
    );
  };

  const handlePlaceDetails = (
    place: google.maps.GeocoderResult,
    isEmployer: boolean,
  ) => {
    const serializedAddress = serializeAddress(i18n, place);

    if (isEmployer) {
      selectEmployerLocationAutoCompleteSuggestion(serializedAddress);
    } else {
      selectWorkLocationAutoCompleteSuggestion(serializedAddress);
    }

    // https://developers.google.com/maps/documentation/javascript/reference/places-widget#Autocomplete.getPlace  -- failure case
    if (!serializedAddress.city) {
      trackError(
        // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
        new RichError('Failed to fetch API result', JSON.stringify(place)),
      );
    }
  };

  return {
    jobTitle: <JobTitleSelectorField searchApiKey={searchApiKey} />,
    managerialStatus: (
      <CheckboxField
        name="managerialStatus"
        label={
          /* TRANSLATORS: Compensation survey */
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {i18n('I supervise(d) employees in this job')}
            <div style={{ position: 'relative', zIndex: 1, marginLeft: '4px' }}>
              <LegacyTooltip>
                {i18n(
                  `Supervision includes part- or full-time employees, as well as interns.`,
                )}
              </LegacyTooltip>
            </div>
          </div>
        }
      />
    ),

    yearsOfExperience: (
      <InputField
        name="yearsOfExperience"
        label={
          /* TRANSLATORS: Compensation survey */
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {i18n('Years of Relevant Experience')}
            {/* TODO: Fix this the next time the file is edited. */}
            {/* oxlint-disable-next-line react/jsx-curly-brace-presence */}
            {'*'}
            <div style={{ whiteSpace: 'normal', marginLeft: '4px' }}>
              <LegacyTooltip>
                {i18n(
                  `For example: if you have 2 years of experience in fundraising and 1 in social work—and the role above is in fundraising—you'd have 2 years of relevant experience.`,
                )}
              </LegacyTooltip>
            </div>
          </div>
        }
        description={i18n(
          'Includes experience in the role listed above and any time spent in similar/transferable roles.',
        )}
        type="number"
        placeholder={i18n('8')}
        min={0}
        max={100}
        noWrap
      />
    ),

    educationLevel: (
      <SelectInputField
        label={`${i18n('Highest level of education completed')}*`}
        name="educationLevel"
        options={V8_EDUCATION_LEVELS_COMP_SURVEY(i18n)}
      />
    ),

    ageBucket: (
      <SelectInputField
        label={i18n('Please select your age (optional)')}
        name="ageBucket"
        options={V8_AGES(i18n)}
      />
    ),

    gender: (
      <MultiCheckboxField
        label={i18n(
          'Please select your gender identity and/or expression (optional)',
        )}
        description={i18n('Select all boxes that apply')}
        name="gender"
        options={V8_GENDERS}
        columns={1}
      />
    ),

    genderFreeText: (
      <FormContextConsumer>
        {({ values }) => {
          const option = V8_GENDERS.find(
            ({ name }: { name: string }) => name === 'OTHER',
          );

          return values.gender &&
            (values.gender as string).includes('OTHER') ? (
            <InputField
              name="genderFreeText"
              aria-label={
                option ? option.label : i18n('Prefer to self describe')
              }
            />
          ) : null;
        }}
      </FormContextConsumer>
    ),

    race: (
      <MultiCheckboxField
        label={i18n('Please select your race and/or ethnicity (optional)')}
        description={i18n('Select all boxes that apply')}
        name="race"
        options={V8_RACES_AND_ETHNICITIES}
        columns={1}
      />
    ),

    raceFreeText: (
      <FormContextConsumer>
        {({ values }) => {
          const option = V8_RACES_AND_ETHNICITIES.find(
            ({ name }: { name: string }) => name === 'OTHER',
          );

          return values.race && (values.race as string).includes('OTHER') ? (
            <InputField
              name="raceFreeText"
              aria-label={
                option
                  ? option.label
                  : i18n('Another race, ethnicity, or origin')
              }
            />
          ) : null;
        }}
      </FormContextConsumer>
    ),

    employerOrgType: (
      <SelectInputField
        label={`${i18n(
          'What best describes the primary work of your organization?',
        )}*`}
        name="employerOrgType"
        options={V8_EMPLOYER_ORG_TYPES(i18n).map(
          ({
            subTypes: _subTypes,
            subTypeLabel: _subTypeLabel,
            ...typeProps
          }) => typeProps,
        )}
      />
    ),

    employerOrgSubType: (
      <FormContextConsumer>
        {({ values }) => {
          const employerOrgType = V8_EMPLOYER_ORG_TYPES(i18n).find(
            (orgType) => orgType.value === values.employerOrgType,
          );
          return values.employerOrgType && employerOrgType?.subTypes ? (
            <SelectInputField
              label={`${i18n('Type of %(orgType)s', {
                orgType: employerOrgType.subTypeLabel,
              })}*`}
              name="employerOrgSubType"
              options={employerOrgType.subTypes}
            />
          ) : null;
        }}
      </FormContextConsumer>
    ),

    employerOrgDescriptor: (
      <FormContextConsumer>
        {({ values }) => {
          const employerOrgType = V8_EMPLOYER_ORG_TYPES(i18n).find(
            (orgType) => orgType.value === values.employerOrgType,
          );
          const employerOrgSubType = employerOrgType?.subTypes
            ? employerOrgType.subTypes.find(
                (orgSubType) => orgSubType.value === values.employerOrgSubType,
              )
            : null;
          // TODO: Fix this the next time the file is edited.
          // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const description =
            // TODO: Fix this the next time the file is edited.
            // @ts-expect-error Property 'description' does not exist on type ...
            employerOrgSubType?.description
              ? // @ts-expect-error Property 'description' does not exist on type ...
                employerOrgSubType.description
              : (employerOrgType?.description ?? null);
          // TODO: Fix this the next time the file is edited.
          // oxlint-disable-next-line @typescript-eslint/no-unsafe-return
          return (
            description && (
              <CompensationSurveyFields_Box_1>
                <BodySmall>{description}</BodySmall>
              </CompensationSurveyFields_Box_1>
            )
          );
        }}
      </FormContextConsumer>
    ),

    employerNEmployeesBucket: (
      <SelectInputField
        label={`${i18n('Number of Employees at Your Organization')}*`}
        description={i18n(
          'Include total employees across all locations, if applicable',
        )}
        noWrap
        name="employerNEmployeesBucket"
        options={V8_NUMBER_OF_FT_EMPLOYEES_BUCKETS(i18n)}
      />
    ),

    location: (
      <>
        <RadioButtonsField
          name="remoteStatus"
          label={
            /* TRANSLATORS: TODO */
            `${i18n('Did you work remotely or on-site for this role?')}*`
          }
          buttons={REMOTE_STATUSES(i18n)}
        />

        <FormContextConsumer>
          {({ values }) => {
            if (!values.remoteStatus) return null;

            return values.remoteStatus === 'REMOTE' ||
              values.remoteStatus === 'HYBRID' ? (
              <>
                <RadioButtonsField
                  name="salaryBasedOn"
                  label={
                    /* TRANSLATORS: TODO */
                    `${i18n(
                      'To the best of your knowledge, your salary is based on',
                    )}*`
                  }
                  buttons={SALARY_BASED_ON_OPTIONS(i18n)}
                />

                {values.salaryBasedOn &&
                values.salaryBasedOn === 'EMPLOYER_LOCATION' ? (
                  <>
                    <div style={{ width: '50%' }}>
                      <EmployerCityStateUnified
                        values={values}
                        handlePlaceDetails={handlePlaceDetails}
                        updateValueWith={updateValueWith}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <WorkCityStateUnified
                        values={values}
                        handlePlaceDetails={handlePlaceDetails}
                        updateValueWith={updateValueWith}
                      />
                    </div>
                  </>
                ) : null}

                {values.salaryBasedOn &&
                values.salaryBasedOn === 'OWN_LOCATION' ? (
                  <>
                    <div style={{ width: '50%' }}>
                      <WorkCityStateUnified
                        values={values}
                        handlePlaceDetails={handlePlaceDetails}
                        updateValueWith={updateValueWith}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <EmployerCityStateUnified
                        values={values}
                        handlePlaceDetails={handlePlaceDetails}
                        updateValueWith={updateValueWith}
                      />
                    </div>
                  </>
                ) : null}
              </>
            ) : (
              <WorkCityStateUnified
                values={values}
                handlePlaceDetails={handlePlaceDetails}
                updateValueWith={updateValueWith}
              />
            );
          }}
        </FormContextConsumer>
      </>
    ),

    annualSalary: (
      <CurrencyInputField
        label={
          /* TRANSLATORS: Compensation survey */
          `${i18n('Annual Salary')}*`
        }
        description={i18n(
          `Enter the most up-to-date salary for the role you selected above.`,
        )}
        name="annualSalary"
        placeholder="10,000"
        min={10000}
        max={10000000}
      />
    ),

    militaryServiceStatus: (
      <RadioButtonsField
        name="militaryServiceStatus"
        label={i18n('I serve or served in the U.S. Military (optional)')}
        buttons={[
          {
            label: i18n('Yes'),
            value: true,
          },
          {
            label: i18n('No'),
            value: false,
          },
        ]}
      />
    ),

    disabilityStatus: (
      <RadioButtonsField
        name="disabilityStatus"
        label={i18n(
          "I'm a person with a disclosed disability that requires an accommodation at work (optional)",
        )}
        buttons={[
          {
            label: i18n('Yes'),
            value: true,
          },
          {
            label: i18n('No'),
            value: false,
          },
        ]}
      />
    ),

    currentlyEmployed: (
      <RadioButtonsField
        name="currentlyEmployed"
        label={i18n(
          "Looks like you don't have a nonprofit job! Are you currently employed elsewhere?",
        )}
        buttons={[
          {
            label: i18n('Yes'),
            value: true,
          },
          {
            label: i18n('No'),
            value: false,
          },
        ]}
      />
    ),

    previouslyEmployed: (
      <RadioButtonsField
        name="previouslyEmployed"
        label={i18n('Have you ever worked for a nonprofit before?')}
        buttons={[
          {
            label: i18n('Yes'),
            value: true,
          },
          {
            label: i18n('No'),
            value: false,
          },
        ]}
      />
    ),

    titleChanged: (
      <FormContextConsumer>
        {({ values }) => (
          <>
            <RadioButtonsField
              name="titleChanged"
              buttons={[
                {
                  label: i18n(
                    'My salary, work location, job title, or employer information has changed.',
                  ),
                  value: true,
                },
                {
                  label: i18n(
                    'There have been no significant changes to my job.',
                  ),
                  value: false,
                },
              ]}
            />

            {values.titleChanged === false && (
              <Info>
                {lastSubmitted
                  ? i18n(
                      'Thank you for your previous submission on %(submittedDate)s! We look forward to receiving an updated survey response when you have significant changes in your job, salary, or location to share.',
                      {
                        submittedDate: humanDate(
                          // This code only runs client-side because
                          // data-fetching happens on the client
                          clientOnlyLocalDateAndTimeString(lastSubmitted).date,
                          { locale, monthFormat: 'short' },
                        ),
                      },
                    )
                  : i18n(
                      'Thank you for your previous submission! We look forward to receiving an updated survey response when you have significant changes in your job, salary, or location to share.',
                    )}
              </Info>
            )}
          </>
        )}
      </FormContextConsumer>
    ),
  };
};
